import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG = "/logo-bookcare.png";

const lines = [
  { text: "Precision in", accent: false },
  { text: "every ledger.", accent: true },
  { text: "Purpose in", accent: false },
  { text: "every career.", accent: true },
];

const stats = [
  { value: "16+", label: "Years in finance leadership" },
  { value: "$14M", label: "Federal grants recovered" },
  { value: "$60M", label: "Budgets built & managed" },
  { value: "50+", label: "Audit findings resolved" },
];

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="mb-8 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
            data-testid="hero-overline"
          >
            BookCare LLC · Accounting · Tax · CFO Advisory
          </motion.p>
          <h1 className="font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-pine md:text-7xl" data-testid="hero-heading">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic text-forest" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.25 + i * 0.12 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-pine/70"
            data-testid="hero-subcopy"
          >
            BookCare LLC pairs 16+ years of nonprofit, profit, and healthcare finance leadership
            with hands-on bookkeeping, tax preparation, and fractional CFO services — protecting
            your profit and coaching new immigrant professionals into thriving finance careers in
            the US market.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href="#contact"
              data-testid="hero-book-consultation-btn"
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center gap-2 border border-forest bg-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest"
            >
              Book a Consultation <ArrowDownRight size={16} />
            </motion.a>
            <motion.a
              href="#services"
              data-testid="hero-explore-services-btn"
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center gap-2 border border-pine/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-pine transition-colors duration-300 hover:border-forest hover:text-forest"
            >
              Explore Services
            </motion.a>
          </motion.div>
        </div>
        <div className="relative md:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay: 0.5 }}
            className="relative aspect-square overflow-hidden border border-linen"
          >
            <motion.img
              src={HERO_IMG}
              alt="BookCare LLC — Nurturing Prudent Decisions"
              data-testid="hero-image"
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.5 }}
              className="h-full w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="absolute -bottom-6 -left-6 border border-linen bg-white/80 px-6 py-4 backdrop-blur-xl"
            data-testid="hero-badge"
          >
            <p className="font-serif text-3xl font-semibold text-forest">16+</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">
              Years · Nonprofit &amp; Healthcare Finance
            </p>
          </motion.div>
        </div>
      </div>
      <div className="mx-auto mt-24 max-w-7xl px-6 pb-20 md:px-10">
        <div className="grid grid-cols-2 gap-px border border-linen bg-linen md:grid-cols-4" data-testid="hero-stats">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.7 }}
              className="bg-bone px-6 py-8"
            >
              <p className="font-serif text-4xl font-semibold text-forest">{s.value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pine/60">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
